/**
 * WORLD-CUP-FIRST-72-HOURS-WARROOM-P0
 *
 * Pure client-side telemetry derivation for the first 72h of activity.
 * Hooks into the existing `vs:event` window event (fired by trackEvent in
 * src/lib/audit.ts), derives milestone events (activation_started,
 * first_prediction_completed, first_share_completed, first_referral_sent,
 * returned_same_day, etc.) and writes them through trackEvent so they
 * land in audit_master like any other event.
 *
 * No new tables. No realtime. No new APIs.
 * State lives in localStorage under `vs:warroom:first72`.
 */
import { trackEvent } from "@/lib/audit";
import type { EventType } from "@/types";
import { toast } from "sonner";

const KEY = "vs:warroom:first72";

export interface First72State {
  firstSeenAt: number | null;
  nationAt: number | null;
  firstPredictionAt: number | null;
  firstShareAt: number | null;
  firstReferralAt: number | null;
  lastSeenDay: string | null; // YYYY-MM-DD
  returnedSameDayLogged: string | null; // day for which we logged a return
  firstRewardSeenAt?: number | null;
}

function defaultState(): First72State {
  return {
    firstSeenAt: null,
    nationAt: null,
    firstPredictionAt: null,
    firstShareAt: null,
    firstReferralAt: null,
    lastSeenDay: null,
    returnedSameDayLogged: null,
  };
}

export function loadFirst72(): First72State {
  if (typeof window === "undefined") return defaultState();
  try {
    const raw = window.localStorage.getItem(KEY);
    if (!raw) return defaultState();
    return { ...defaultState(), ...(JSON.parse(raw) as Partial<First72State>) };
  } catch {
    return defaultState();
  }
}

function save(s: First72State) {
  try { window.localStorage.setItem(KEY, JSON.stringify(s)); } catch { /* noop */ }
}

function ymd(t: number): string {
  const d = new Date(t);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

export function firstPredictionBucket(ms: number): "0-10s" | "10-30s" | "30-60s" | "60-120s" | "120s+" {
  const s = Math.max(0, Math.round(ms / 1000));
  if (s <= 10) return "0-10s";
  if (s <= 30) return "10-30s";
  if (s <= 60) return "30-60s";
  if (s <= 120) return "60-120s";
  return "120s+";
}

let booted = false;

export function bootstrapFirst72Telemetry(): void {
  if (typeof window === "undefined" || booted) return;
  booted = true;

  // Mark activation_started on the very first session this device sees.
  const initial = loadFirst72();
  const now = Date.now();
  if (!initial.firstSeenAt) {
    initial.firstSeenAt = now;
    initial.lastSeenDay = ymd(now);
    save(initial);
    void trackEvent("activation_started", { at: now });
  } else {
    // Same-day return detection — only fire once per day.
    const today = ymd(now);
    if (initial.lastSeenDay && initial.lastSeenDay !== today) {
      // different day → just update lastSeenDay
      initial.lastSeenDay = today;
      save(initial);
    } else if (initial.lastSeenDay === today) {
      // same calendar day as last visit — only counts as return if there was
      // a gap of at least 1h since first_seen, to avoid same-session noise.
      const gap = now - initial.firstSeenAt;
      if (gap > 60 * 60 * 1000 && initial.returnedSameDayLogged !== today) {
        initial.returnedSameDayLogged = today;
        save(initial);
        void trackEvent("returned_same_day", { day: today });
      }
    }
  }

  const handler = (e: Event) => {
    const detail = (e as CustomEvent<{ event_type: EventType; event_data: Record<string, unknown> }>).detail;
    if (!detail) return;
    const { event_type, event_data } = detail;
    const s = loadFirst72();
    const t = Date.now();

    if (event_type === "nation_selected" && !s.nationAt) {
      s.nationAt = t;
      save(s);
    }

    if (event_type === "prediction_vote" && !s.firstPredictionAt) {
      s.firstPredictionAt = t;
      save(s);
      const elapsed = s.firstSeenAt ? t - s.firstSeenAt : 0;
      void trackEvent("first_prediction_completed", {
        elapsed_ms: elapsed,
        bucket: firstPredictionBucket(elapsed),
      });
      // WORLD-CUP-FINAL-POLISH-P0 — immediate reward feedback after the
      // first prediction. Single toast, single event, idempotent.
      if (!s.firstRewardSeenAt) {
        s.firstRewardSeenAt = t;
        save(s);
        try {
          toast("🏆 +50 puntos — sumaste a tu nación", {
            description: "🔥 Bienvenido al Top de fans del Mundial",
            duration: 5000,
          });
        } catch { /* noop */ }
        void trackEvent("first_reward_seen", {
          reward: "+50",
          surface: "first_prediction",
        });
      }
    }

    if (
      (event_type === "viral_share_completed" || event_type === "share_success_feedback_shown") &&
      !s.firstShareAt
    ) {
      s.firstShareAt = t;
      save(s);
      void trackEvent("first_share_completed", {
        channel: (event_data?.channel as string) ?? "unknown",
      });
    }

    if (event_type === "share_link_generated" && !s.firstReferralAt) {
      s.firstReferralAt = t;
      save(s);
      void trackEvent("first_referral_sent", {
        code: (event_data?.code as string) ?? null,
      });
    }

    if (event_type === "daily_wow_card_action") {
      const action = (event_data?.action as string) ?? "";
      if (action === "share" || action === "shared") {
        void trackEvent("daily_wow_shared", { source: "daily_wow" });
      }
    }

    if (event_type === "challenge_friend_clicked") {
      void trackEvent("challenge_friend_sent", { ...event_data });
    }
    if (event_type === "challenge_friend_status_changed") {
      const status = (event_data?.status as string) ?? "";
      if (status === "accepted") {
        void trackEvent("challenge_friend_accepted", { ...event_data });
      }
    }
  };

  window.addEventListener("vs:event", handler as EventListener);
}
